import {
  collection,
  CollectionReference,
  doc,
  DocumentData,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import {
  ICrossTestMetricSelection,
  ISelectableTag,
  ISelectableTestType,
  IUser,
} from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface IAsymmetryReportSettings {
  mode: IAsymmetryModeOption;
  testType: ISelectableTestType;
  leftTag?: ISelectableTag;
  rightTag?: ISelectableTag;
  tags: ISelectableTag[];
  metrics: ICrossTestMetricSelection[];
}

export interface IAsymmetryModeOption {
  label: 'Bilateral' | 'Unilateral';
  value: 'bilateral' | 'unilateral';
}

export const asymmetryModeOptions: IAsymmetryModeOption[] = [
  { label: 'Unilateral', value: 'unilateral' },
  { label: 'Bilateral', value: 'bilateral' },
];

/**
 * @description the subset of the asymmetry settings that are not template specific
 */
interface IAsymmetryReportSettingOptions {
  timeframe: IDateRange;
  baselineTimeframe: IDateRange;
}

/**
 * @description the complete document in the database
 */
interface IAsymmetryReportSettingEntity extends IAsymmetryReportSettingOptions {
  selected: ISettingsTemplate<IAsymmetryReportSettings> | null;
}

/**
 * @description the interface that is emitted by the provider
 */
export interface IAsymmetryReportSettingsState
  extends IAsymmetryReportSettingEntity {
  templates: Array<ISettingsTemplate<IAsymmetryReportSettings>>;
}

/**
 * @description observes the asymmetry report settings and related settings templates
 */
export const asymmetryReportSettingsProvider =
  (): Observable<IAsymmetryReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap((user: IUser): Observable<IAsymmetryReportSettingsState> => {
        return combineLatest([
          selectedTemplateProvider({
            userId: user.id!,
            firestore: user.services.firestore,
          }),
          templatesProvider({
            userId: user.id!,
            firestore: user.services.firestore,
          }),
          asymmetrySettingsOptionsProvider({
            userId: user.id!,
            firestore: user.services.firestore,
          }),
        ]).pipe(
          map(
            (
              result: [
                ISettingsTemplate<IAsymmetryReportSettings>,
                Array<ISettingsTemplate<IAsymmetryReportSettings>>,
                IAsymmetryReportSettingOptions
              ]
            ): IAsymmetryReportSettingsState => {
              const [selected, templates, options] = result;
              if (selected?.id) {
                return {
                  selected,
                  templates,
                  ...options,
                };
              } else {
                return {
                  selected: null,
                  templates,
                  ...options,
                };
              }
            }
          )
        );
      })
    );
  };

interface IProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: IProviderArgs
): Observable<ISettingsTemplate<IAsymmetryReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(firestore, `userProfiles/${userId}/settings/asymmetryReport`)
  ).pipe(
    map(
      (
        entity: DocumentData | undefined
      ): ISettingsTemplate<IAsymmetryReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IAsymmetryReportSettings>,
        curr: ISettingsTemplate<IAsymmetryReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes changes the subset of the asymmetry settings that are not template specific
 * @param userId
 * @returns
 */
const asymmetrySettingsOptionsProvider = (
  args: IProviderArgs
): Observable<IAsymmetryReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData(
    doc(firestore, `userProfiles/${userId}/settings/asymmetryReport`)
  ).pipe(
    map(
      (
        entity: IAsymmetryReportSettingEntity
      ): IAsymmetryReportSettingOptions => ({
        timeframe: entity?.timeframe,
        baselineTimeframe: entity?.baselineTimeframe,
      })
    ),
    distinctUntilChanged(
      (
        prev: IAsymmetryReportSettingOptions,
        curr: IAsymmetryReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: IProviderArgs
): Observable<Array<ISettingsTemplate<IAsymmetryReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData<ISettingsTemplate<IAsymmetryReportSettings>>(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/asymmetryReport/templates`
      ) as CollectionReference<ISettingsTemplate<IAsymmetryReportSettings>>,
      where('active', '==', true)
    )
  );
};
